import { css } from "@emotion/react";
import { get } from "aws-amplify/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Accordion, Button, Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import HybridProofingCategory from "./HybridProofingCategory";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory } from "utils/elementIds";

function HybridProofingDetail({ location, isAuthenticated, user }) {
  const routerLocation = useLocation();

  const detailIdPrefix = "hybrid-proofing-detail";
  const createId = useMemo(() => createElementIdFactory(detailIdPrefix), [detailIdPrefix]);
  const filterControlHeight = 38;
  const filterSelectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: filterControlHeight,
        height: filterControlHeight,
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: filterControlHeight,
        padding: "0 8px",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: filterControlHeight,
      }),
      input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
      }),
    }),
    []
  );

  const isSLM = useMemo(() => {
    const pathname = routerLocation?.pathname || location?.pathname || "";
    return pathname.toLowerCase().startsWith("/slm");
  }, [routerLocation?.pathname, location?.pathname]);
  const [isLoading, setIsLoading] = useState(null);
  const [queueTypes, setQueueTypes] = useState([]);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [searchRun, setSearchRun] = useState(false);
  const [queueType, setQueueType] = useState(null);
  const queueTypeResearchTeam = isSLM ? "SLM" : "Cityscope";
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [queueAction, setQueueAction] = useState("");
  const [userActioned, setUserActioned] = useState("");
  const [dateActionedFrom, setDateActionedFrom] = useState(null);
  const [dateActionedTo, setDateActionedTo] = useState(null);
  const [userClosed, setUserClosed] = useState("");
  const [dateClosedFrom, setDateClosedFrom] = useState(null);
  const [dateClosedTo, setDateClosedTo] = useState(null);
  const [itemCategories, setItemCategories] = useState([]);
  const [queueTypeDropdown, setQueueTypeDropdown] = useState(null);
  const [submittedFilter, setSubmittedFilter] = useState(null);


  // --- Data Fetching and Update Functions ---
  async function getCityscopes() {
    const response = await get({
      apiName: "colin",
      path: "/cityscopes/dropdown",
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }


  const getQueueTypes = useCallback(async () => {
    const options = {
      queryParams: {
        research_team: queueTypeResearchTeam,
      },
    };
    const response = await get({
      apiName: "colin",
      path: "/proofing/queue_types",
      options,
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, [queueTypeResearchTeam]);


  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const localCityscopeOptions = await getCityscopes();
        if (localCityscopeOptions) {
          setCityscopeOptions(localCityscopeOptions);
        }
        const localqueueTypes = await getQueueTypes();
        if (localqueueTypes) {
          setQueueTypes(localqueueTypes);
          setQueueTypeDropdown(
            localqueueTypes.map((t) => ({
              value: t.queue_type_id,
              label: t.queue_type,
            }))
          );
        }
        document.title = `Ad-hoc Proofing Queues (${isSLM ? "SLM" : "Cityscope"})`;
        setIsLoading(false);
      } catch (e) {
        alert(e);
      }
    })();
  }, [isSLM, getQueueTypes]);


  async function getItemCategories(filter) {
    const options = {
      queryParams: filter,
    };
    const response = await get({
      apiName: "colin",
      path: "/proofing/queues",
      options,
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }


  const handleChange = (event) => {
    const { id, name, value } = event.target;
    const field = name || id;

    if (field === "queueAction") {
      setQueueAction(value);
      return;
    }

    if (field === "userActioned") {
      setUserActioned(value);
      return;
    }

    if (field === "userClosed") {
      setUserClosed(value);
    }
  };

  function isSelectedValue(value) {
    if (value === true || value === 1) return true;
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "y" || normalized === "yes";
    }
    return false;
  }


  const handleQueueTypeChange = async (queueType) => {
    setQueueType(queueType);
  };


  const handleCityscopeChange = async (selectedCityscope) => {
    if (!selectedCityscope || selectedCityscope.length === 0) {
      setSelectedCityscope(null);
      return;
    }
    setSelectedCityscope(selectedCityscope);
  };


  function handleDateChange(date, name) {
    if (name === "dateActionedFrom") {
      setDateActionedFrom(date || null);
      return;
    }

    if (name === "dateActionedTo") {
      setDateActionedTo(date || null);
      return;
    }

    if (name === "dateClosedFrom") {
      setDateClosedFrom(date || null);
      return;
    }

    if (name === "dateClosedTo") {
      setDateClosedTo(date || null);
    }
  }

  function toLocalDayStartISOString(date) {
    if (!date) return "";
    const localStart = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0
    );
    return localStart.toISOString();
  }

  function toLocalDayEndISOString(date) {
    if (!date) return "";
    const localEnd = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    );
    return localEnd.toISOString();
  }

  function buildFilter() {
    return {
      queue_type_id: queueType !== null ? queueType.value : "",
      queue_type_research_team: queueTypeResearchTeam,
      cityscope: (selectedCityscope || []).map((c) => c.value).join(","),
      action: queueAction || "",
      user_actioned: userActioned || "",
      date_actioned_from: toLocalDayStartISOString(dateActionedFrom),
      date_actioned_to: toLocalDayEndISOString(dateActionedTo),
      user_closed: userClosed || "",
      date_closed_from: toLocalDayStartISOString(dateClosedFrom),
      date_closed_to: toLocalDayEndISOString(dateClosedTo),
    };
  }

  function mapToItemCategories(itemCategoriesResponse) {
    const grouped = Object.entries(
      itemCategoriesResponse.reduce((rv, x) => {
        const record = { ...x, selected: isSelectedValue(x.selected) };
        (rv[record.queue_type_id] = rv[record.queue_type_id] || []).push(record);
        return rv;
      }, {})
    ).map(([queue_type_id, queues]) => {
      const queueTypeRecord = queueTypes.find(
        (t) => String(t.queue_type_id) === String(queue_type_id)
      );

      console.assert(
        queueTypeRecord !== null,
        "No matching queueType for queues:",
        queues
      );

      return {
        id: queue_type_id,
        name: queueTypeRecord ? queueTypeRecord.queue_type : null,
        description: queueTypeRecord ? queueTypeRecord.description : null,
        items: queues,
      };
    });

    return grouped;
  }

  async function refreshCategories(filter) {
    const itemCategoriesResponse = await getItemCategories(filter);

    setItemCategories([]);

    if (itemCategoriesResponse === null) {
      alert("Unable to fetch results.");
      return;
    }

    setItemCategories(mapToItemCategories(itemCategoriesResponse));
    setSearchRun(true);
  }


  const handleSearch = async () => {
    setIsLoading(true);
    const filter = buildFilter();
    setSubmittedFilter(filter);
    await refreshCategories(filter);
    setIsLoading(false);
  };

  async function handleRefreshRequested(showLoading = false) {
    const filter = submittedFilter || buildFilter();
    if (showLoading) {
      setIsLoading(true);
    }
    await refreshCategories(filter);
    if (showLoading) {
      setIsLoading(false);
    }
  }


  // --- Render Functions ---

  function render() {
    return (
      <div id={createId("proofing")} className="Proofing">
        {isLoading && (
          <div id={createId("loader", "outer")} className="outer-div">
            <div id={createId("loader", "inner")} className="sweet-loading inner-div">
              <PacmanLoader
                css={css`
                  display: block;
                  margin: 0 auto;
                  border-color: red;
                `}
                size={20}
                color={"#e03c31"}
                loading={isLoading}
              />
            </div>
          </div>
        )}

        {cityscopeOptions && !isLoading && (
          <div id={createId("form", "container")} className="ProofingForm">
            <Row id={createId("title", "row")}>
              <Col id={createId("title", "col")} sm={12} md={12} lg={12}>
                <h6 id={createId("title")}>Ad-hoc Proofing Queues ({isSLM ? "SLM" : "Cityscope"})</h6>
              </Col>
            </Row>
            <Form id={createId("filter", "form")} className="mb-4">
              <Row id={createId("filter", "row", "1")} className="g-3 mb-3">
                <Col id={createId("queue", "type", "col")} lg={2}>
                  <Form.Group controlId={createId("queue", "type", "group")}>
                    <Select
                      value={queueType}
                      options={queueTypeDropdown}
                      placeholder="Queue Type"
                      onChange={handleQueueTypeChange}
                      inputId={createId("queue", "type", "input")}
                      instanceId={createId("queue", "type", "instance")}
                      styles={filterSelectStyles}
                    />
                  </Form.Group>
                </Col>

                {!isSLM && (
                  <Col id={createId("cityscope", "col")} lg={2}>
                    <Form.Group controlId={createId("cityscope", "group")}>
                      <Select
                        value={selectedCityscope}
                        onChange={handleCityscopeChange}
                        options={cityscopeOptions}
                        placeholder="Cityscope"
                        isMulti
                        inputId={createId("cityscope", "input")}
                        instanceId={createId("cityscope", "instance")}
                        styles={filterSelectStyles}
                      />
                    </Form.Group>
                  </Col>
                )}

                <Col id={createId("queue", "action", "col")} lg={2}>
                  <Form.Group controlId={createId("queue", "action", "group")}>
                    <Form.Control
                      id={createId("queue", "action", "input")}
                      name="queueAction"
                      value={queueAction}
                      placeholder="Queue Action"
                      type="input"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row id={createId("filter", "row", "2")} className="g-3 mb-3">
                <Col id={createId("user", "actioned", "col")} lg={2}>
                  <Form.Group controlId={createId("user", "actioned", "group")}>
                    <Form.Control
                      id={createId("user", "actioned", "input")}
                      name="userActioned"
                      value={userActioned}
                      placeholder="User that Actioned"
                      type="input"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col id={createId("date", "actioned", "col")} lg={2}>
                  <Row id={createId("date", "actioned", "row")}>
                    <Col id={createId("date", "actioned", "from", "col")} className="pr-0">
                      <Form.Group controlId={createId("date", "actioned", "from", "group")}>
                        <DatePicker
                          id={createId("date", "actioned", "from", "input")}
                          selected={dateActionedFrom}
                          onChange={(e) =>
                            handleDateChange(e, "dateActionedFrom")
                          }
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Date Actioned from"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                    <Col id={createId("date", "actioned", "to", "col")} className="pl-0">
                      <Form.Group
                        controlId={createId("date", "actioned", "to", "group")}
                        className="float-right"
                      >
                        <DatePicker
                          id={createId("date", "actioned", "to", "input")}
                          selected={dateActionedTo}
                          onChange={(e) =>
                            handleDateChange(e, "dateActionedTo")
                          }
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Date Actioned to"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>

                <Col id={createId("user", "closed", "col")} lg={2}>
                  <Form.Group controlId={createId("user", "closed", "group")}>
                    <Form.Control
                      id={createId("user", "closed", "input")}
                      name="userClosed"
                      value={userClosed}
                      placeholder="User that Closed"
                      type="input"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col id={createId("date", "closed", "col")} lg={2}>
                  <Row id={createId("date", "closed", "row")}>
                    <Col id={createId("date", "closed", "from", "col")} className="pr-0">
                      <Form.Group controlId={createId("date", "closed", "from", "group")}>
                        <DatePicker
                          id={createId("date", "closed", "from", "input")}
                          selected={dateClosedFrom}
                          onChange={(e) =>
                            handleDateChange(e, "dateClosedFrom")
                          }
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Date Closed from"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                    <Col id={createId("date", "closed", "to", "col")} className="pl-0">
                      <Form.Group
                        controlId={createId("date", "closed", "to", "group")}
                        className="float-right"
                      >
                        <DatePicker
                          id={createId("date", "closed", "to", "input")}
                          selected={dateClosedTo}
                          onChange={(e) => handleDateChange(e, "dateClosedTo")}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Date Closed to"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>

                <Col id={createId("search", "col")} lg={4}>
                  <Button id={createId("search", "button")} variant="primary" onClick={handleSearch}>
                    Search
                  </Button>
                  <br />
                </Col>
              </Row>
            </Form>
            <div id={createId("results")} className="proofResults">
              {searchRun && itemCategories.length === 0 && (
                <h4 id={createId("empty", "results")} className="text-center">No results to display.</h4>
              )}
              <Accordion id={createId("accordion")} className="proofing-accordion" defaultActiveKey="0">
                {itemCategories.map((c) => {
                  return (
                    <HybridProofingCategory
                      key={c.id}
                      id={c.id}
                      name={c.name}
                      description={c.description}
                      items={c.items}
                      user={user}
                      isAuthenticated={isAuthenticated}
                      onRefreshRequested={handleRefreshRequested}
                    ></HybridProofingCategory>
                  );
                })}
              </Accordion>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (!isAuthenticated) return null;

  return <div id={createId("container")} className="ProofingDetail">{render()}</div>;
}

export default HybridProofingDetail;
